import { differenceInMinutes, format } from 'date-fns';
import { fromZonedTime } from 'date-fns-tz';
import { useLoaderData, useSearchParams } from 'react-router';
import { Result } from 'ts-results-es';
import { z } from 'zod';
import { zx } from 'zodix';

import { BeltAttendanceIndicator } from '~/components/belt-attendance-indicator';
import { getHints } from '~/components/client-hints';
import { EmptySetMessage } from '~/components/content/empty-set-message';
import { getAthleteGymClassOccurrencesForDay } from '~/domain/gym-classes/handlers/get-athlete-gym-class-occurrences-for-day.server';
import { type AthleteGymClassDayOccurrence } from '~/domain/gym-classes/models/gym-class-occurrences';
import { getAthleteGymClasses } from '~/domain/gym-classes/queries/get-athlete-gym-classes.server';
import { parseDate, parseDateTime, parseTime, serializeDate } from '~/domain/shared/time-helpers';
import ClassDateSelect from '~/routes/admin+/gyms+/$gymId+/classes+/components/class-date-select';
import { getSessionIfAuthenticated } from '~/utils/auth.server';
import { handleDomainError } from '~/utils/errors.server';
import { getGymIdAndSlugFromParams } from '~/utils/gyms.server';

import { RsvpButton } from '../components/rsvp-button';

import { type Route } from './+types/_index';

const enum Params {
  Date = 'date',
}

export async function loader({ request, params }: Route.LoaderArgs) {
  const session = await getSessionIfAuthenticated(request);

  const { gymId } = await getGymIdAndSlugFromParams(params);
  const { timeZone } = getHints(request);

  const { date } = zx.parseQuery(request, {
    [Params.Date]: z.string().default(() => serializeDate(new Date(), timeZone)),
  });

  const queryResults = await Promise.all([
    getAthleteGymClasses(gymId),
    getAthleteGymClassOccurrencesForDay(gymId, date, session),
  ]);
  const result = Result.all(...queryResults);

  return result
    .map(([gymClasses, { gymClassOccurrences, gymTimeZone }]) => ({
      gymId,
      gymClasses,
      gymClassOccurrences,
      gymTimeZone,
      timeZone,
    }))
    .mapErr(handleDomainError)
    .unwrap();
}

export default function Calendar() {
  const { gymId, gymClasses, gymTimeZone, gymClassOccurrences, timeZone } =
    useLoaderData<typeof loader>();
  const now = serializeDate(new Date(), timeZone);
  const defaultSearchParams = new URLSearchParams();
  defaultSearchParams.set(Params.Date, now);
  const [searchParams, setSearchParams] = useSearchParams(defaultSearchParams);

  const rawSearchDate = searchParams.get(Params.Date) ?? now;
  const searchDate = parseDate(rawSearchDate);

  const handleSelectedDateChanged = (date: Date) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set(Params.Date, serializeDate(date, timeZone));
    setSearchParams(newParams);
  };

  return (
    <>
      {gymClasses.length > 0 ? (
        <>
          <ClassDateSelect
            dateStart={searchDate}
            selectedDate={searchDate}
            onSelectedDateChanged={handleSelectedDateChanged}
          />
          {gymClassOccurrences.length > 0 ? (
            <div className="flex flex-col py-3">
              {gymClassOccurrences.map((occurrence) => (
                <GymClassOccurrence
                  key={`${occurrence.id}-${occurrence.startTime}-${occurrence.endTime}`}
                  gymId={gymId}
                  gymTimeZone={gymTimeZone}
                  date={rawSearchDate}
                  occurrence={occurrence}
                />
              ))}
            </div>
          ) : (
            <EmptySetMessage
              title="No classes scheduled"
              titleClassName="text-subtitle1"
              message="Check other dates for available classes."
              className="p-0 pt-16 text-center"
            />
          )}
        </>
      ) : (
        <EmptySetMessage
          title="No information entered"
          titleClassName="text-subtitle1"
          message="Contact the gym or visit their website to get the latest class information."
          className="p-0 pt-6 text-center"
        />
      )}
    </>
  );
}

interface GymClassOccurrenceProps {
  gymId: string;
  gymTimeZone: string;
  date: string;
  occurrence: AthleteGymClassDayOccurrence;
}

const formatTime = (time: string) => {
  const parsed = parseTime(time);
  return format(parsed, 'hh:mm aa');
};

const formatDuration = (start: string, end: string) => {
  const startTime = parseTime(start);
  const endTime = parseTime(end);
  const minutes = differenceInMinutes(endTime, startTime);
  return `${minutes} min`;
};

function GymClassOccurrence({ gymId, gymTimeZone, date, occurrence }: GymClassOccurrenceProps) {
  const now = Date.now();
  const occurrenceStart = fromZonedTime(
    parseDateTime(date, occurrence.startTime),
    gymTimeZone,
  ).getTime();

  return (
    <div className="grid grid-cols-[1fr_2fr_1fr] items-center gap-2 border-b-2 border-b-gray-200 p-4">
      <div className="font-mono text-subtitle2 font-extrabold">
        {formatTime(occurrence.startTime)}
      </div>

      <div className="text-subtitle1">{occurrence.name}</div>

      <div className="col-start-3 row-span-3 w-full self-start justify-self-center">
        <RsvpButton
          gymClassName={occurrence.name}
          gymId={gymId}
          gymClassId={occurrence.gymClassId}
          date={date}
          startTime={occurrence.startTime}
          endTime={occurrence.endTime}
          rsvpId={occurrence.rsvpId}
          disabled={now >= occurrenceStart}
        />
      </div>

      <div className="font-mono text-subtitle2 text-primary-dark">
        {formatDuration(occurrence.startTime, occurrence.endTime)}
      </div>

      <div className="text-body2">
        {occurrence.instructorFirstName} {occurrence.instructorLastName}
      </div>

      <div className="col-start-2">
        <BeltAttendanceIndicator countsByBelt={occurrence.beltCounts ?? []} />
      </div>
    </div>
  );
}
